/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {SignalLike} from '../signal-like/signal-like';
import {
  EventHandler,
  EventHandlerOptions,
  EventManager,
  hasModifiers,
  ModifierInputs,
  Modifier,
} from './event-manager';

/**
 * Used to represent a keycode.
 *
 * This is used to match whether an events keycode should be handled. The ability to match using a
 * string, SignalLike, or Regexp gives us more flexibility when authoring event handlers.
 */
type KeyCode = string | SignalLike<string> | RegExp;

/**
 * An event manager that is specialized for handling keyboard events. By default this manager stops
 * propagation and prevents default on all events it handles.
 */
export class KeyboardEventManager<T extends KeyboardEvent> extends EventManager<T> {
  options: EventHandlerOptions = {
    ignoreRepeat: true,
    preventDefault: true,
    stopPropagation: true,
  };

  /** Configures this event manager to handle events with a specific key and no modifiers. */
  on(key: KeyCode, handler: EventHandler<T>, options?: Partial<EventHandlerOptions>): this;

  /**  Configures this event manager to handle events with a specific modifer and key combination. */
  on(
    modifiers: ModifierInputs,
    key: KeyCode,
    handler: EventHandler<T>,
    options?: Partial<EventHandlerOptions>,
  ): this;

  on(...args: any[]) {
    const {modifiers, key, handler, options} = this._normalizeInputs(...args);

    this.configs.push({
      handler: handler,
      matcher: event => this._isMatch(event, key, modifiers, options),
      ...this.options,
      ...options,
    });

    return this;
  }

  private _normalizeInputs(...args: any[]) {
    const withModifiers = Array.isArray(args[0]) || args[0] in Modifier;
    const modifiers = withModifiers ? args[0] : Modifier.None;
    const key = withModifiers ? args[1] : args[0];
    const handler = withModifiers ? args[2] : args[1];
    const options = withModifiers ? args[3] : args[2];

    return {
      key: key as KeyCode,
      handler: handler as EventHandler<T>,
      modifiers: modifiers as ModifierInputs,
      options: (options ?? {}) as Partial<EventHandlerOptions>,
    };
  }

  private _isMatch(
    event: T,
    key: KeyCode,
    modifiers: ModifierInputs,
    options?: Partial<EventHandlerOptions>,
  ): boolean {
    if (!hasModifiers(event, modifiers)) {
      return false;
    }

    // Default is to ignore repeated key events unless explicitly set to false.
    if (event.repeat && options?.ignoreRepeat !== false) {
      return false;
    }

    if (key instanceof RegExp) {
      return key.test(event.key);
    }

    const keyStr = typeof key === 'string' ? key : key();
    return keyStr.toLowerCase() === event.key.toLowerCase();
  }
}
